/**
 * Manual Asset Entry Form
 * TASK-210: Portfolio Management Module - Frontend Prototype
 * TASK-277: Removed mock dependency - uses local extraction simulation
 *
 * Form for adding alternative assets (real estate, art, crypto, PE) to portfolios
 * with simulated document extraction (pending real AI extraction API)
 */

import React, { useState } from 'react';
import {
  X,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle2,
  Building,
  Palette,
  Bitcoin,
  TrendingUp,
  FileCheck,
  Edit3,
  Loader2
} from 'lucide-react';
// TASK-277: Type-only import - no mock functions
import type { MockManualAsset } from '../../services/mock/portfolioMockData';
import { toast } from 'sonner';

/**
 * TASK-277: Local asset extraction simulation
 * TODO: Replace with real AI document extraction API when available
 */
const simulateAssetExtraction = async (
  assetType: string,
  fileName: string
): Promise<Partial<{
  assetName: string;
  description: string;
  acquisitionCost: number;
  acquisitionDate: string;
  currentValuation: number;
  valuationCurrency: string;
  valuationDate: string;
  valuationMethod: string;
  extractionConfidence: number;
}>> => {
  // Simulate processing delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  // Generate sample extraction based on asset type
  const baseNames: Record<string, string[]> = {
    real_estate: ['Apartment - Zurich', 'Villa - Geneva', 'Chalet - Verbier', 'Townhouse - Basel'],
    art: ['Oil Painting - 19th Century', 'Bronze Sculpture', 'Contemporary Art Piece', 'Antique Furniture'],
    cryptocurrency: ['Bitcoin Holdings', 'Ethereum Portfolio', 'Multi-Coin Wallet', 'DeFi Position'],
    private_equity: ['Startup Fund Stake', 'PE Fund Commitment', 'Venture Capital Position', 'Growth Equity'],
    other: ['Alternative Investment', 'Structured Product', 'Special Asset', 'Miscellaneous Holding']
  };

  const names = baseNames[assetType] || baseNames.other;
  const randomName = names[Math.floor(Math.random() * names.length)];

  return {
    assetName: `${randomName} (from ${fileName})`,
    description: `Extracted from document: ${fileName}`,
    acquisitionCost: Math.round(Math.random() * 500000) + 100000,
    acquisitionDate: '2022-06-15',
    currentValuation: Math.round(Math.random() * 800000) + 200000,
    valuationCurrency: 'CHF',
    valuationDate: new Date().toISOString().split('T')[0],
    valuationMethod: 'appraisal',
    extractionConfidence: 0.85 + Math.random() * 0.12
  };
};

interface ManualAssetEntryFormProps {
  isOpen: boolean;
  portfolioId: string;
  portfolioName: string;
  onClose: () => void;
  onSave: (asset: Omit<MockManualAsset, 'id'>) => void;
}

type AssetType = 'real_estate' | 'art' | 'private_equity' | 'cryptocurrency' | 'other';
type EntryMode = 'document' | 'manual';
type FormStage = 'type_selection' | 'entry_mode' | 'document_upload' | 'extracting' | 'review' | 'manual_entry';

interface FormData {
  assetType: AssetType;
  assetName: string;
  description: string;
  acquisitionCost: number;
  acquisitionDate: string;
  currentValuation: number;
  valuationCurrency: 'CHF' | 'EUR' | 'USD';
  valuationDate: string;
  valuationMethod: 'appraisal' | 'cost' | 'market' | 'estimate';
  supportingDocuments?: string[];
  extractionConfidence?: number;
}

const ManualAssetEntryForm: React.FC<ManualAssetEntryFormProps> = ({
  isOpen,
  portfolioId: _portfolioId,
  portfolioName,
  onClose,
  onSave
}) => {
  const [stage, setStage] = useState<FormStage>('type_selection');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractionProgress, setExtractionProgress] = useState(0);

  const [formData, setFormData] = useState<FormData>({
    assetType: 'real_estate',
    assetName: '',
    description: '',
    acquisitionCost: 0,
    acquisitionDate: '',
    currentValuation: 0,
    valuationCurrency: 'CHF',
    valuationDate: new Date().toISOString().split('T')[0],
    valuationMethod: 'estimate'
  });

  // Reset form when closed
  const handleClose = () => {
    setStage('type_selection');
    setSelectedFile(null);
    setExtractionProgress(0);
    setFormData({
      assetType: 'real_estate',
      assetName: '',
      description: '',
      acquisitionCost: 0,
      acquisitionDate: '',
      currentValuation: 0,
      valuationCurrency: 'CHF',
      valuationDate: new Date().toISOString().split('T')[0],
      valuationMethod: 'estimate'
    });
    onClose();
  };

  // Asset type configuration
  const assetTypes = [
    {
      type: 'real_estate' as const,
      label: 'Real Estate',
      icon: Building,
      color: 'blue',
      description: 'Properties, land, REITs'
    },
    {
      type: 'art' as const,
      label: 'Art & Collectibles',
      icon: Palette,
      color: 'purple',
      description: 'Paintings, sculptures, antiques'
    },
    {
      type: 'cryptocurrency' as const,
      label: 'Cryptocurrency',
      icon: Bitcoin,
      color: 'orange',
      description: 'Bitcoin, Ethereum, etc.'
    },
    {
      type: 'private_equity' as const,
      label: 'Private Equity',
      icon: TrendingUp,
      color: 'green',
      description: 'VC funds, PE investments'
    },
    {
      type: 'other' as const,
      label: 'Other Assets',
      icon: FileText,
      color: 'gray',
      description: 'Other alternative investments'
    }
  ];

  const selectedAssetType = assetTypes.find(at => at.type === formData.assetType);

  // Handle asset type selection
  const handleTypeSelect = (type: AssetType) => {
    setFormData(prev => ({ ...prev, assetType: type }));
    setStage('entry_mode');
  };

  // Handle entry mode selection
  const handleModeSelect = (mode: EntryMode) => {
    if (mode === 'document') {
      setStage('document_upload');
    } else {
      setStage('manual_entry');
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Handle document extraction
  const handleExtract = async () => {
    if (!selectedFile) return;

    setStage('extracting');
    setExtractionProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setExtractionProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      // Simulate extraction
      const extractedData = await simulateAssetExtraction(formData.assetType, selectedFile.name);

      setExtractionProgress(100);
      clearInterval(progressInterval);

      // Populate form with extracted data
      setFormData({
        ...formData,
        assetName: extractedData.assetName || formData.assetName,
        description: extractedData.description || formData.description,
        acquisitionCost: extractedData.acquisitionCost || formData.acquisitionCost,
        acquisitionDate: extractedData.acquisitionDate || formData.acquisitionDate,
        currentValuation: extractedData.currentValuation || formData.currentValuation,
        valuationDate: extractedData.valuationDate || formData.valuationDate,
        valuationMethod: (extractedData.valuationMethod as 'appraisal' | 'cost' | 'market' | 'estimate') || formData.valuationMethod,
        valuationCurrency: (extractedData.valuationCurrency as 'CHF' | 'EUR' | 'USD') || formData.valuationCurrency,
        extractionConfidence: extractedData.extractionConfidence,
        supportingDocuments: [selectedFile.name]
      });

      setStage('review');

      toast.success('Document analyzed successfully!');
    } catch (error) {
      clearInterval(progressInterval);
      toast.error('Failed to extract data from document');
      setStage('document_upload');
    }
  };

  // Handle form field changes
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle save
  const handleSave = () => {
    // Validate required fields
    if (!formData.assetName || !formData.currentValuation) {
      toast.error('Please fill in all required fields');
      return;
    }

    const asset: Omit<MockManualAsset, 'id'> = {
      assetType: formData.assetType,
      assetName: formData.assetName,
      description: formData.description,
      acquisitionCost: formData.acquisitionCost,
      acquisitionDate: formData.acquisitionDate,
      currentValuation: formData.currentValuation,
      valuationCurrency: formData.valuationCurrency,
      valuationDate: formData.valuationDate,
      valuationMethod: formData.valuationMethod,
      supportingDocuments: formData.supportingDocuments,
      extractionConfidence: formData.extractionConfidence
    };

    onSave(asset);
    toast.success('Alternative asset added successfully!');
    handleClose();
  };

  // Handle back navigation
  const handleBack = () => {
    if (stage === 'entry_mode') {
      setStage('type_selection');
    } else if (stage === 'document_upload' || stage === 'manual_entry') {
      setStage('entry_mode');
      setSelectedFile(null);
    } else if (stage === 'review') {
      setStage('document_upload');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Alternative Asset</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Portfolio: {portfolioName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Stage 1: Asset Type Selection */}
          {stage === 'type_selection' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Select Asset Type
                </h3>
                <p className="text-sm text-gray-600">
                  Choose the type of alternative asset you want to add
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {assetTypes.map((assetType) => (
                  <button
                    key={assetType.type}
                    onClick={() => handleTypeSelect(assetType.type)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                  >
                    <div className={`w-12 h-12 bg-${assetType.color}-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-${assetType.color}-200 transition-colors`}>
                      <assetType.icon className={`w-6 h-6 text-${assetType.color}-600`} />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {assetType.label}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {assetType.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stage 2: Entry Mode Selection */}
          {stage === 'entry_mode' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
                  {selectedAssetType && <selectedAssetType.icon className="w-8 h-8 text-blue-600" />}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {selectedAssetType?.label}
                </h3>
                <p className="text-sm text-gray-600">
                  How would you like to add this asset?
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <button
                  onClick={() => handleModeSelect('document')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Upload className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Upload Document
                      </h4>
                      <p className="text-sm text-gray-600">
                        Upload a valuation report, appraisal, or certificate. We'll extract the key information automatically.
                      </p>
                      <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-600 font-medium">
                        <FileCheck className="w-4 h-4" />
                        AI-powered extraction
                      </div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleModeSelect('manual')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Edit3 className="w-6 h-6 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Manual Entry
                      </h4>
                      <p className="text-sm text-gray-600">
                        Enter asset details manually using a form. Best for when you have all the information at hand.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Stage 3: Document Upload */}
          {stage === 'document_upload' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Upload Document
                </h3>
                <p className="text-sm text-gray-600">
                  Upload a valuation report, appraisal, certificate, or statement
                </p>
              </div>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all">
                <input
                  type="file"
                  id="document-upload"
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="hidden"
                />
                <label
                  htmlFor="document-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, DOC, DOCX, JPG, PNG (max 10MB)
                  </p>
                </label>
              </div>

              {selectedFile && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-gray-600">
                    <p className="font-medium text-gray-900 mb-1">Document Extraction</p>
                    <p>
                      Our AI will analyze the document and extract key information such as asset name,
                      valuation, acquisition details, and more. You'll be able to review and edit the
                      extracted data before saving.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stage 4: Extracting */}
          {stage === 'extracting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Analyzing Document...
              </h3>
              <p className="text-sm text-gray-600 mb-6 text-center max-w-md">
                We're extracting key information from your document. This may take a few seconds.
              </p>

              {/* Progress Bar */}
              <div className="w-full max-w-md">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Progress</span>
                  <span>{extractionProgress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${extractionProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Stage 5: Review Extracted Data */}
          {stage === 'review' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900 mb-1">
                      Document Analyzed Successfully
                    </p>
                    <p className="text-xs text-green-700">
                      Review the extracted information below and make any necessary corrections.
                      Confidence: {((formData.extractionConfidence || 0) * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    value={formData.assetName}
                    onChange={(e) => handleFieldChange('assetName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Chalet - Verbier"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional details about the asset..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Valuation *
                    </label>
                    <input
                      type="number"
                      value={formData.currentValuation}
                      onChange={(e) => handleFieldChange('currentValuation', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={formData.valuationCurrency}
                      onChange={(e) => handleFieldChange('valuationCurrency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="CHF">CHF</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Acquisition Cost
                    </label>
                    <input
                      type="number"
                      value={formData.acquisitionCost}
                      onChange={(e) => handleFieldChange('acquisitionCost', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Acquisition Date
                    </label>
                    <input
                      type="date"
                      value={formData.acquisitionDate}
                      onChange={(e) => handleFieldChange('acquisitionDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valuation Date
                    </label>
                    <input
                      type="date"
                      value={formData.valuationDate}
                      onChange={(e) => handleFieldChange('valuationDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valuation Method
                    </label>
                    <select
                      value={formData.valuationMethod}
                      onChange={(e) => handleFieldChange('valuationMethod', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="appraisal">Appraisal</option>
                      <option value="market">Market Value</option>
                      <option value="cost">Cost Basis</option>
                      <option value="estimate">Estimate</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stage 6: Manual Entry */}
          {stage === 'manual_entry' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-3">
                  {selectedAssetType && <selectedAssetType.icon className={`w-8 h-8 text-${selectedAssetType.color}-600`} />}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {selectedAssetType?.label} Details
                </h3>
                <p className="text-sm text-gray-600">
                  Enter the asset information manually
                </p>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    value={formData.assetName}
                    onChange={(e) => handleFieldChange('assetName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Chalet - Verbier"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional details about the asset..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Valuation *
                    </label>
                    <input
                      type="number"
                      value={formData.currentValuation}
                      onChange={(e) => handleFieldChange('currentValuation', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={formData.valuationCurrency}
                      onChange={(e) => handleFieldChange('valuationCurrency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="CHF">CHF</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Acquisition Cost
                    </label>
                    <input
                      type="number"
                      value={formData.acquisitionCost}
                      onChange={(e) => handleFieldChange('acquisitionCost', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Acquisition Date
                    </label>
                    <input
                      type="date"
                      value={formData.acquisitionDate}
                      onChange={(e) => handleFieldChange('acquisitionDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valuation Date
                    </label>
                    <input
                      type="date"
                      value={formData.valuationDate}
                      onChange={(e) => handleFieldChange('valuationDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valuation Method
                    </label>
                    <select
                      value={formData.valuationMethod}
                      onChange={(e) => handleFieldChange('valuationMethod', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="appraisal">Appraisal</option>
                      <option value="market">Market Value</option>
                      <option value="cost">Cost Basis</option>
                      <option value="estimate">Estimate</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div>
            {(stage !== 'type_selection' && stage !== 'extracting') && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
              >
                Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              Cancel
            </button>

            {stage === 'document_upload' && (
              <button
                onClick={handleExtract}
                disabled={!selectedFile}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Extract Data
              </button>
            )}

            {(stage === 'review' || stage === 'manual_entry') && (
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Save Asset
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualAssetEntryForm;
